import { MapPin, Person, Phone } from '../../../../modules/design-system';
import type { ShippingAddress } from '../../../../modules/api';

export interface AddressSectionProps {
  address: ShippingAddress | null;
}

/**
 * Read-only address on the Show page — Figma 361:132067. Renders the
 * name / short address / phone as inline rows with leading icons (no
 * outer box around them — they sit directly on the Delivery card).
 */
export function AddressSection({ address }: AddressSectionProps) {
  return (
    <section>
      <h3
        className="text-[17px] font-bold tracking-[-0.3px]"
        style={{ color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)' }}
      >
        Your Address
      </h3>

      {address === null ? (
        <p
          className="mt-3 text-[14px]"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          Address not available for this delivery.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2 text-[14px] text-white">
          <Row icon={<Person size={18} strokeWidth={1.6} />} text={address.name} />
          <Row
            icon={<MapPin size={18} strokeWidth={1.6} />}
            text={formatShortAddress(address)}
          />
          {address.phoneNumber && (
            <Row
              icon={<Phone size={18} strokeWidth={1.6} />}
              text={address.phoneNumber}
            />
          )}
        </ul>
      )}
    </section>
  );
}

function Row({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <li className="flex items-center gap-2.5 leading-[20px]">
      <span
        className="inline-flex shrink-0 items-center justify-center"
        style={{ color: 'rgba(255,255,255,0.75)' }}
      >
        {icon}
      </span>
      <span className="min-w-0">{text}</span>
    </li>
  );
}

function formatShortAddress(a: ShippingAddress): string {
  const cityState = [a.city, a.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, a.zipcode].filter(Boolean).join(' ');
  return [cityStateZip, a.country].filter(Boolean).join(', ');
}
